import React, { useEffect, useMemo, useState, useRef } from 'react'
import cx from 'classnames'
import vsbl from 'vsbl'
import BlockContent from 'src/components/global/BlockContent'

import { Image } from 'src/components/image'
import { createSerializer } from 'src/utils/serializer'
import { getPortableTextHeading } from 'src/utils/ctaTracking'
import {
  VideoAspectRatio,
  VideoCornerRadius,
  VideoDisplay,
  VideoFile,
  VideoObjectFit,
  VideoPlayer,
  VideoPosterImage,
  VideoProvider,
  VideoSourceType,
} from 'src/components/global/videoPlayer'

type ImageCropPosition =
  | 'hotspot'
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'

const imageCropObjectPositions: Record<
  Exclude<ImageCropPosition, 'hotspot'>,
  string
> = {
  center: 'center center',
  top: 'center top',
  bottom: 'center bottom',
  left: 'left center',
  right: 'right center',
}

export interface TextAndMediaProps {
  data: {
    text: any[]
    backgroundColor: string
    mediaType?: 'image' | 'video'
    image?: {
      alt?: string
      hotspot?: {
        x?: number
        y?: number
      }
      asset: {
        _id: string
        _ref?: string
      }
    }
    mobileImage?: {
      alt?: string
      asset: {
        _id: string
        _ref?: string
      }
    }
    // Video fields
    videoSourceType?: VideoSourceType
    videoProvider?: VideoProvider
    videoUrl?: string
    videoFile?: VideoFile
    videoTitle?: string
    vimeoVideo?: string
    youtubeVideo?: string
    videoPosterImage?: VideoPosterImage
    autoPlay?: boolean
    muted?: boolean
    loop?: boolean
    showControls?: boolean
    posterAlt?: string
    aspectRatio?: VideoAspectRatio
    mobileAspectRatio?: VideoAspectRatio
    videoDisplay?: VideoDisplay
    cornerRadius?: VideoCornerRadius
    objectFit?: VideoObjectFit
    textAboveMedia?: boolean
    textAboveImage?: boolean
    roundCorners?: boolean
    matchImageHeightToText?: boolean
    imageCropPosition?: ImageCropPosition
    hideImageOnMobile?: boolean
    centerTextOnMobile?: boolean
    mediaGap?: 'none' | 'sm' | 'md' | 'lg'
    mediaGapMobile?: 'none' | 'sm' | 'md' | 'lg'
    caption?: string
    alignment?: string
    spacing: string
    style: string
    options?: {
      uid?: string
    }
  }
}

export const TextAndMedia = ({ data }: TextAndMediaProps) => {
  const {
    text,
    backgroundColor,
    mediaType = 'image',
    image,
    alignment,
    spacing,
    style,
    mobileImage,
    textAboveImage,
    textAboveMedia,
    roundCorners = false,
    matchImageHeightToText = false,
    imageCropPosition = 'center',
    hideImageOnMobile = false,
    centerTextOnMobile = false,
    mediaGap,
    mediaGapMobile,
    caption,
    videoSourceType,
    videoProvider,
    videoUrl,
    videoFile,
    videoTitle,
    vimeoVideo,
    youtubeVideo,
    videoPosterImage,
    autoPlay = false,
    muted = false,
    loop = false,
    showControls = true,
    posterAlt,
    aspectRatio = '16:9',
    mobileAspectRatio,
    videoDisplay = 'container',
    cornerRadius = 'md',
    objectFit = 'cover',
    options,
  } = data

  const textAbove = textAboveMedia || textAboveImage || false

  const dom = useRef<HTMLDivElement>(null)
  const [className, setClass] = useState('')
  const serializers = useMemo(
    () =>
      createSerializer({
        module: 'Text + media module',
        heading: getPortableTextHeading(text, 'Text + media'),
      }),
    [text]
  )

  useEffect(() => {
    const { current } = dom
    const vs = vsbl(current)(() => {
      setClass('is-visible')
    })
    vs.update()
    return () => {
      vs.destroy()
    }
  }, [])

  const desktopImageIdentifier = image?.asset?._id || image?.asset?._ref
  const mobileImageIdentifier =
    mobileImage?.asset?._id || mobileImage?.asset?._ref
  const imageAlt = image?.alt || mobileImage?.alt || caption || ''
  const resolvedMobileGap = mediaGapMobile || mediaGap
  const shouldMatchImageHeight =
    mediaType === 'image' && style !== 'card' && matchImageHeightToText
  const imageObjectPosition =
    imageCropPosition === 'hotspot'
      ? `${(image?.hotspot?.x ?? 0.5) * 100}% ${
          (image?.hotspot?.y ?? 0.5) * 100
        }%`
      : imageCropObjectPositions[imageCropPosition]
  const shouldAutoPlayVideo =
    mediaType === 'video' && autoPlay && className === 'is-visible'

  const renderMedia = () => {
    if (mediaType === 'video') {
      return (
        <VideoPlayer
          sourceType={videoSourceType}
          provider={videoProvider}
          url={videoUrl}
          file={videoFile}
          legacyYoutubeUrl={youtubeVideo}
          legacyVimeoUrl={vimeoVideo}
          posterImage={videoPosterImage}
          posterAlt={posterAlt}
          title={videoTitle}
          autoPlay={shouldAutoPlayVideo}
          muted={muted}
          loop={loop}
          showControls={showControls}
          aspectRatio={aspectRatio}
          mobileAspectRatio={mobileAspectRatio}
          display={videoDisplay}
          cornerRadius={cornerRadius}
          objectFit={objectFit}
        />
      )
    }

    return (
      <Image
        imageId={desktopImageIdentifier || mobileImageIdentifier}
        mobileImageId={mobileImageIdentifier}
        alt={imageAlt}
      />
    )
  }

  return (
    <div
      ref={dom}
      id={options?.uid}
      className={cx('image__text', className, backgroundColor, spacing, {
        cw: backgroundColor === 'bcb',
        'image__text--text-above-mobile': textAbove,
        'image__text--center-mobile': centerTextOnMobile,
        'image__text--match-image-height': shouldMatchImageHeight,
        [`image__text--gap-${mediaGap}`]: mediaGap,
        [`image__text--mobile-gap-${resolvedMobileGap}`]: resolvedMobileGap,
      })}
    >
      <div className="container--l outer mxa al x">
        <div
          className={cx('f jcb fw image__text-layout', alignment, style, {
            jcs: style === 'card' && alignment !== 'jcrr',
            aic: style !== 'card',
            aist: style === 'card',
          })}
        >
          <div
            className={cx('x rel animate opacity d2 slide-up image__text-img', {
              'col-l-5': style === 'card',
              'col-m-4': style !== 'card',
              'image__text-img--rounded': mediaType === 'image' && roundCorners,
              'image__text-img--hide-mobile':
                mediaType === 'image' && hideImageOnMobile,
            })}
            style={
              shouldMatchImageHeight
                ? ({
                    '--image-text-crop-position': imageObjectPosition,
                  } as React.CSSProperties)
                : undefined
            }
          >
            {renderMedia()}
          </div>
          <div
            className={cx('x animate opacity d3 slide-up image__text-content', {
              'col-l-4 p1': style === 'card',
              'col-m-6': style !== 'card',
            })}
          >
            <BlockContent blocks={text} serializers={serializers} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default TextAndMedia
